import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "../../utility/useAuth";
import { useStoreActions, useStoreState } from "../../store";
import {
  getCurrentUser,
  getGraphMe,
  getSupportTenant,
  isAccessDenied,
  isAccountUnavailable,
  isTokenRejection,
} from "../../services/ApiService";
import {
  requiredCorpAccessGroups,
  resolveCorpAccess,
} from "../../services/corpAccessRuntime";
import { readAuthCache, writeAuthCache } from "../../utility/authCache";
import { DefaultLayout } from "../../layouts/Default";
import Spinner from "../../components/Spinner/Spinner";
import { logger } from "../../utility/logger";
import { resolveHomeRegion } from "../../config/regionMap";
import { setActiveRegion, setHomeRegion } from "../../services/regionRuntime";
import { isSupportAutoTenantEnabled } from "../../utility/supportTenantAccess";
import { isCorpWhoto, resolveAutoTenant } from "../../utility/supportTenant";
import { applySupportTarget } from "../../services/supportTenantRuntime";
import { isAnalyticsEnabled } from "../../utility/analyticsAccess";
import {
  ANALYTICS_PATH,
  analyticsOnlyDecision,
  isAnalyticsOnlyAccess,
} from "../../utility/analyticsOnlyAccess";
import { useAccess } from "../../access/AccessProvider";
import { AccessGate, AccountUnavailable } from "../../access/AccessGate";

enum AuthState {
  LOADING,
  AUTHORISED,
  AUTHENTICATED_NOT_LICENCED,
  UNAUTHORISED,
  // The access check could not be COMPLETED (network, 5xx, Graph unavailable). Distinct from
  // AUTHENTICATED_NOT_LICENCED, which is a real answer: "we checked, and you don't have
  // access". Previously every error took the licensing branch, so an expired token or a
  // Function App blip both told the user their account was denied access.
  AUTH_CHECK_FAILED,
  // `401 ACCOUNT_UNAVAILABLE` from a licensing read: the token is good and the account behind
  // it cannot be used. Neither a failed check (nothing is wrong with the service) nor a stale
  // token (signing in again comes back to the same answer).
  ACCOUNT_UNAVAILABLE,
}

interface IPrivateRoute {
  children: React.ReactElement;
}

/**
 * Normalize values for comparison
 */
function normalize(value?: string) {
  return value?.trim().toLowerCase() ?? "";
}

export const PrivateRoute = (props: IPrivateRoute) => {
  const location = useLocation();
  const auth = useAuth();

  const [authorised, setAuthorised] = useState<AuthState>(AuthState.LOADING);

  // ✅ Holds values to display when access is denied
  const [deniedInfo, setDeniedInfo] = useState<{
    companyName?: string;
    whoto?: string;
    // Set when the refusal was one of the CORP licensing gates rather than a plain
    // company/whoto mismatch. The two need different wording: a mismatch is about which
    // environment the account belongs to, this is about a corp user failing one of the two
    // live Entra checks.
    groupDenied?: boolean;
    requiredGroups?: string[];
    // The BFF's machine-readable reason, when the refusal came from the BFF rather than from
    // this client's own group check. It distinguishes the two 403s that are otherwise
    // identical on screen — CORP_GROUP_REQUIRED (not in the group) from
    // CORP_COMPANY_REQUIRED (Entra companyName is not on the corp allow-list). Conflating
    // them sent people to IT to be re-added to a group they were already in, while the real
    // cause — a companyName typo, or a value nobody had ever seen — went unexamined.
    reasonCode?: string;
  } | null>(null);

  const { user: userProfile } = useStoreActions((actions) => actions);

  // `licensingPassed` is stable across renders: the effect below runs once and calls it from
  // there. The rest is read for the Analytics-only confinement below.
  const access = useAccess();
  const { licensingPassed } = access;

  // Only the site's code, for the "nothing available" screen. Who the user is for access comes
  // from `access`, never from the role bits in this record.
  const user = useStoreState((s: any) => s.user);

useEffect(() => {
  let isMounted = true;

  async function runAuth() {
    // ✅ 1. Check cache first (validated: current version + unexpired; corrupt/stale ⇒ null,
    //    so we simply fall through to the authoritative flow below).
    const parsed = readAuthCache();

    if (parsed) {
      if (!isMounted) return;

      // ✅ Restore store state
      userProfile.setAuthenticated(parsed.isAuthenticated);
      userProfile.setEmail(parsed.email);
      userProfile.setFirstName(parsed.firstName);
      userProfile.setLastName(parsed.lastName);
      userProfile.setFullName(parsed.fullName);
      userProfile.setCompany(parsed.company);
      userProfile.setLanguage("en");
      userProfile.setInfo(parsed.userInfo);

      // Re-select the home region on the cached path too — runtime BFF selection is
      // module state that a page reload resets, so it must be re-applied even when the
      // full auth flow is skipped. setHomeRegion records where the user's OWN row lives
      // (for the self-scoped calls) and survives the impersonation applied just below,
      // which only moves the ACTIVE region.
      setHomeRegion(resolveHomeRegion(parsed.company));
      setActiveRegion(resolveHomeRegion(parsed.company));

      // Same reason for the CORP support impersonation: it is module state, so a reload
      // would otherwise drop the user back into CORP while the header still claimed
      // otherwise. Re-validated (flag + WHOTO + known org), so a tampered cache entry can
      // only be ignored — never widen access. Overrides the region set just above when it
      // resolves, because the supported org may live in another region.
      //
      // `unprovisioned` is what keeps a support-bootstrap session alive across a reload: that
      // user has no row of their own, so without the override re-applied here every screen
      // would come back empty. It cannot widen access — the BFF re-authorizes the override on
      // every request, and a forged flag only makes a normal user impersonate a support
      // account they would then be refused.
      const cachedTarget = resolveAutoTenant({
        featureEnabled: isSupportAutoTenantEnabled(),
        whoto: parsed.userInfo?.whoto,
        savedTenant: parsed.supportTenant,
        unprovisioned: parsed.userInfo?.supportBootstrap === true,
      });

      if (cachedTarget) {
        applySupportTarget(cachedTarget);
      }

      // GET api/me is read on every page load, the cached one included: what the user may do
      // is never cached. Called after the support target, so it is asked in the session's
      // final organisation. On a later mount in the same scope it reads nothing.
      if (parsed.isAuthenticated) {
        licensingPassed();
      }

      setAuthorised(
        parsed.isAuthenticated
          ? AuthState.AUTHORISED
          : AuthState.UNAUTHORISED
      );

      return; // 🚀 SKIP API CALLS
    }

    // ✅ 2. No cache → run full flow
    const user = await auth.getUser();

    if (!user || !user.access_token) {
      setAuthorised(AuthState.UNAUTHORISED);
      return;
    }

    // Hoisted out of the try so the catch can still report WHICH company was refused. The
    // BFF's denial arrives as a thrown AccessDeniedError from getCurrentUser, at which point
    // the in-scope `graphUser` is gone — which is why that branch used to print
    // "Unavailable" for the one field that identifies the problem.
    let graphCompanyName: string | undefined;

    try {
      const graphUser = await getGraphMe(user.access_token);

      graphCompanyName = graphUser?.companyName?.trim() || undefined;

      // ✅ Point the API + realtime layers at the user's HOME region BEFORE the first
      //    BFF call. The region is derived from the global Graph companyName (a regular
      //    user's companyName is their org code). Corp-capable / unmapped users resolve
      //    to undefined and fall back to the default Function App.
      //
      //    Both slots are set: the ACTIVE region is what every data call follows (and what
      //    an org switch moves), while the HOME region records where this user's own row
      //    lives, so the self-scoped calls (TOS acceptance, the support-tenant preference)
      //    keep hitting their own database even while they support another org.
      setHomeRegion(resolveHomeRegion(graphUser?.companyName));
      setActiveRegion(resolveHomeRegion(graphUser?.companyName));

      const userInfo = await getCurrentUser(auth);

      const graphCompanyNorm = normalize(graphUser?.companyName);
      const backendWhotoNorm = normalize(userInfo?.whoto);

      const standardMatch = graphCompanyNorm === backendWhotoNorm;

      // Corp-capable Graph companyName values. Mirrors CORP_CAPABLE_COMPANIES in
      // config/regionMap.ts and SupportBootstrapPolicy.SupportCompanies in the Function App —
      // all four copies must be changed together or the halves of the gate disagree.
      // Not all of them are Swagelok: "k2 venture partners llc" is a third-party vendor
      // (added 2026-09-18). Being listed here is eligibility, not access — the per-environment
      // Entra group check below is what actually admits them.
      const swagelokAllowedCompanies = [
        "swagelok manufacturing",
        "swagelok annuity",
        "isle of man",
        "k2 venture partners llc",
      ];

      const swagelokCorpOverride =
        swagelokAllowedCompanies.includes(graphCompanyNorm) &&
        (backendWhotoNorm === "corp" ||
          backendWhotoNorm === "corp2");

      if (!standardMatch && !swagelokCorpOverride) {
        setDeniedInfo({
          companyName: graphUser?.companyName ?? "Unknown",
          whoto: userInfo?.whoto ?? "Unknown",
        });

        setAuthorised(AuthState.AUTHENTICATED_NOT_LICENCED);
        return;
      }

      // ✅ Second licensing gate, for CORP/CORP2 users only: this environment's Entra access
      //    group (CSM-NP-QA-Swg-access on dev/QA, CSM-P-Swg-access on production). Corp users
      //    are the ones who roam across every tenant, and nothing in their identity says
      //    WHICH environment they belong to — the corp-capable companyName list is identical
      //    in every environment and whoto only says CORP — so the environment has to name the group it
      //    admits. Regular SSC operators are unaffected: their companyName === whoto match is
      //    already environment-specific, because their user row only exists in one database.
      //
      //    Keyed on the WHOTO, not on which branch above licensed them, so it covers a corp
      //    user however they were admitted. A missing group configuration means no gate.
      //
      //    resolveCorpAccess THROWS when Graph can't be reached; that falls into the catch
      //    below and becomes "we couldn't verify your access", never a denial.
      if (isCorpWhoto(userInfo?.whoto)) {
        const corpAccess = await resolveCorpAccess(
          user.access_token,
          user.profile as Record<string, unknown> | undefined
        );

        if (corpAccess === "denied") {
          logger.warn(
            "CORP user denied: not a member of this environment's access group."
          );

          setDeniedInfo({
            companyName: graphUser?.companyName ?? "Unknown",
            whoto: userInfo?.whoto ?? "Unknown",
            groupDenied: true,
            requiredGroups: requiredCorpAccessGroups(),
          });

          setAuthorised(AuthState.AUTHENTICATED_NOT_LICENCED);
          return;
        }
      }

      // ✅ Licensing has passed. Only now may a CORP/CORP2 user be dropped into the
      //    organization they last supported (feature-flagged). Fetching is gated on the
      //    flag AND the WHOTO so a non-corp user never even issues the request; the
      //    endpoint refuses them anyway, and resolveAutoTenant refuses them a third time
      //    below. A failure here (feature off server-side, no row, network) is not fatal —
      //    log it and log the user into their own tenant as before.
      let supportTenant: string | null = null;

      // A support-bootstrap user (no dbo.Users row, admitted by this environment's access
      // group) is skipped: the saved tenant lives on dbo.Users.LastSupportTenant, so there is
      // no row to read it from and the endpoint refuses them by design. They always start in
      // CORP and switch from there — see resolveAutoTenant.
      const bootstrapped = userInfo?.supportBootstrap === true;

      if (
        !bootstrapped &&
        isSupportAutoTenantEnabled() &&
        isCorpWhoto(userInfo?.whoto)
      ) {
        try {
          const saved = await getSupportTenant(auth);
          supportTenant = saved?.tenant ?? null;
        } catch (error) {
          logger.warn(
            "Support auto-tenant lookup failed; continuing in the user's own tenant.",
            error
          );
        }
      }

      const autoTarget = resolveAutoTenant({
        featureEnabled: isSupportAutoTenantEnabled(),
        whoto: userInfo?.whoto,
        savedTenant: supportTenant,
        // Not a preference for this user — it is the only identity they have, so it is
        // applied regardless of the auto-tenant flag.
        unprovisioned: bootstrapped,
      });

      if (autoTarget) {
        applySupportTarget(autoTarget);
      }

      const profile: any = user.profile ?? null;

      // ✅ Build cache object.
      // Identity fields (email / name) are sourced from Microsoft Graph first —
      // it is the authoritative, reliably-populated source (`mail` / `displayName`
      // are already fetched by getGraphMe). The OIDC id_token claims are kept only
      // as a fallback: their claim keys are IdP-dependent and have proven brittle
      // (e.g. `full_name` is non-standard), which previously left the diagnostic
      // feedback email with a blank associate name + email.
      const cachePayload = {
        isAuthenticated: !!profile,
        email:
          (graphUser?.mail?.trim() ||
            graphUser?.userPrincipalName?.trim() ||
            profile?.email?.trim()) ??
          "",
        firstName: profile?.given_name?.trim() ?? "",
        lastName: profile?.family_name?.trim() ?? "",
        fullName:
          (graphUser?.displayName?.trim() || profile?.full_name?.trim()) ?? "",
        company: graphUser?.companyName?.trim() ?? "",
        userInfo,
        // Only the org actually applied, so the cached path restores exactly this session
        // (never a saved-but-rejected code).
        supportTenant: autoTarget?.code ?? null,
      };

      // ✅ Save to store
      userProfile.setAuthenticated(cachePayload.isAuthenticated);
      userProfile.setEmail(cachePayload.email);
      userProfile.setFirstName(cachePayload.firstName);
      userProfile.setLastName(cachePayload.lastName);
      userProfile.setFullName(cachePayload.fullName);
      userProfile.setCompany(cachePayload.company);
      userProfile.setLanguage("en");
      userProfile.setInfo(cachePayload.userInfo);

      // ✅ Save to session cache (stamped with cache version + timestamp)
      writeAuthCache(cachePayload);

      if (!isMounted) return;

      // Licensing has passed and the support target is applied, so GET api/me is asked in the
      // organisation the session will act in.
      if (user.id_token) {
        licensingPassed();
      }

      setAuthorised(
        user.id_token
          ? AuthState.AUTHORISED
          : AuthState.UNAUTHORISED
      );
    } catch (error) {
      if (!isMounted) return;

      // A rejected token is NOT a licensing failure. Route it back through /login so the
      // user gets a fresh interactive sign-in (ApiService escalates BFF 401s the same way).
      // This is the case that used to render "Authentication Error: Access Denied" with both
      // fields showing "Unavailable" — for a token that had merely expired.
      // The BFF refuses a non-member CORP user at api/users/current as well, so that the
      // gate holds even if this client is bypassed. That answer is a DECISION, so it must be
      // checked before isTokenRejection — which claims every 403 and would redirect to
      // /login, where the IdP hands back a perfectly valid token and the cycle repeats.
      if (isAccessDenied(error)) {
        logger.warn(
          `Access denied by the backend licensing check (${error.code}); companyName "${
            graphCompanyName ?? "(none)"
          }".`,
          error
        );
        setDeniedInfo({
          // Graph answered before the BFF refused us, so this is the real value — and for a
          // CORP_COMPANY_REQUIRED refusal it IS the reason. `whoto` genuinely is unknown:
          // getCurrentUser is the call that threw, so the backend never told us one.
          companyName: graphCompanyName ?? "Unavailable",
          whoto: "Unavailable",
          groupDenied: true,
          requiredGroups: requiredCorpAccessGroups(),
          reasonCode: error.code,
        });
        setAuthorised(AuthState.AUTHENTICATED_NOT_LICENCED);
        return;
      }

      // A page load with no auth cache reads licensing before GET api/me, so the access gate
      // never sees this answer. It gets the gate's own screen: Sign out, no renewal, no login.
      if (isAccountUnavailable(error)) {
        logger.warn("This account cannot be used here (licensing check).", error);
        setAuthorised(AuthState.ACCOUNT_UNAVAILABLE);
        return;
      }

      if (isTokenRejection(error)) {
        logger.warn("Access token rejected during the auth check; signing in again.", error);
        setAuthorised(AuthState.UNAUTHORISED);
        return;
      }

      // Anything else (network, 5xx, Graph outage) means we could not determine access at
      // all — say exactly that rather than accusing the account.
      logger.error("Authentication / authorization check failed", error);
      setAuthorised(AuthState.AUTH_CHECK_FAILED);
    }
  }

  runAuth();

  return () => {
    isMounted = false;
  };
}, []); // ✅ RUN ONLY ONCE

  // -------- Render logic --------

  if (authorised === AuthState.LOADING) {
    return <Spinner />;
  }

  if (authorised === AuthState.AUTHENTICATED_NOT_LICENCED) {
    return (
      <DefaultLayout>
        <div className="text-danger">
          <h1>Authentication Error: Access Denied</h1>

          {deniedInfo?.groupDenied ? (
            <>
              {/* Two different refusals reach this branch and they need different
                  instructions. CORP_COMPANY_REQUIRED is not a group problem at all, and
                  telling someone it is sends them to IT to be re-added to a group they are
                  already in while the actual cause — the companyName below — goes unread. */}
              {deniedInfo?.reasonCode === "CORP_COMPANY_REQUIRED" ? (
                <p>
                  Your account is authenticated, but the company recorded on your
                  Entra (Azure AD) profile is not one that may use CSM as a corp
                  user. Ask IT to check the <strong>Company name</strong> on your
                  directory profile against the value below — the match is exact.
                </p>
              ) : (
                <p>
                  Your account is authenticated, but it is not a member of the
                  security group that grants access to this CSM environment. Ask
                  IT to add you to that group.
                </p>
              )}

              <ul>
                {/* Shown for BOTH refusals on purpose. It is the reason for one of them, and
                    for the other it is the single most useful thing to quote to IT — an
                    unexpected value here means the group was never the problem. */}
                <li>
                  <strong>Company (Entra):</strong>{" "}
                  {deniedInfo?.companyName || "Unavailable"}
                </li>
                <li>
                  <strong>Assigned Environment:</strong> {deniedInfo?.whoto}
                </li>
                {/* The group id is for whoever handles the request — it is the only
                    part of this that identifies WHICH environment refused them. */}
                <li>
                  <strong>Required group:</strong>{" "}
                  {deniedInfo?.requiredGroups?.length
                    ? deniedInfo.requiredGroups.join(", ")
                    : "Unavailable"}
                </li>
                {deniedInfo?.reasonCode ? (
                  <li>
                    <strong>Reason code:</strong> {deniedInfo.reasonCode}
                  </li>
                ) : null}
              </ul>
            </>
          ) : (
            <>
              <p>
                Your account is authenticated, but your user does not have access to the authenticated environment
              </p>

              <ul>
                <li>
                  <strong>Authentication:</strong>{" "}
                  {deniedInfo?.companyName}
                </li>
                <li>
                  <strong>Assigned Environment:</strong>{" "}
                  {deniedInfo?.whoto}
                </li>
              </ul>
            </>
          )}

        </div>
      </DefaultLayout>
    );
  }

  if (authorised === AuthState.AUTH_CHECK_FAILED) {
    return (
      <DefaultLayout>
        <div className="text-danger">
          <h1>We couldn&apos;t verify your access</h1>

          <p>
            CSM could not reach the service that confirms your access. This is usually
            temporary and is not a problem with your account.
          </p>

          <button type="button" onClick={() => window.location.reload()}>
            Try again
          </button>{" "}
          <button type="button" onClick={() => auth.login()}>
            Sign in again
          </button>
        </div>
      </DefaultLayout>
    );
  }

  if (authorised === AuthState.ACCOUNT_UNAVAILABLE) {
    return <AccountUnavailable />;
  }

  if (authorised === AuthState.UNAUTHORISED) {
    return (
      <Navigate
        to={{ pathname: "/login" }}
        replace
        state={{ from: location }}
      />
    );
  }

  // -------- Access --------
  //
  // Licensing has passed. What the user may do comes from GET api/me, and until the provider
  // has an answer it can use, the gate renders in place of whatever this route would render:
  // no route child, no Analytics-only redirect, no tab or button decided on access that is not
  // known. Every protected route comes through here, so a route added later is covered too.
  const gated = (route: React.ReactElement) => <AccessGate>{route}</AccessGate>;

  // -------- Analytics-only users --------
  //
  // An active account with NEITHER access bit does not consume one of the site's seats, and in
  // exchange the app is closed to it except for Analytics — where it can open and run the saved
  // reports shared with it. Everything else would be an empty screen: the boards, the ribbons
  // and the settings pages are all built around holding one of those two bits.
  //
  // It is read from GET api/me: an answer in use that lets the user run reports and not see
  // jobs. Loading, failed and a refused switch are not that answer, and confine nobody: a
  // manager whose read failed is shown the gate's screen, never sent to Analytics. After a
  // refused switch the gate lets the org picker through, and this must not then send the user
  // from the picker to Analytics.
  //
  // ⚠️ THIS LIVES IN PrivateRoute, NOT IN A PER-ROUTE WRAPPER, AND THAT IS THE POINT. Every
  // protected screen in App.tsx already goes through here, so a route added later is confined
  // automatically. A wrapper beside each route's CapabilityRoute would have to be remembered thirty times
  // and would be silently missing on the thirty-first.
  //
  // ⚠️ It runs AFTER the licensing states above, so a user licensing refused is shown the
  // Access Denied screen, not Analytics. No access read is started for that user either.
  const analyticsOnly = analyticsOnlyDecision({
    isAnalyticsOnly: isAnalyticsOnlyAccess(access),
    analyticsEnabled: isAnalyticsEnabled(),
    canSwitchOrganization: access.can("switchOrganization"),
    pathname: location.pathname,
  });

  if (analyticsOnly === "redirect") {
    return gated(<Navigate to={ANALYTICS_PATH} replace />);
  }

  if (analyticsOnly === "unavailable") {
    // Analytics is switched off in this environment, so there is genuinely nowhere to send
    // them — and redirecting to /Analytics would be bounced straight back to /home by
    // AnalyticsRoute, forever. Say what is wrong instead.
    return gated(
      <DefaultLayout>
        <div className="text-danger">
          <h1>Nothing is available for your account</h1>

          <p>
            Your account is signed in, but it has neither <strong>Manager</strong> nor{" "}
            <strong>Shop Floor</strong> access, and Reporting is not switched on in this
            environment. Ask a manager at your site to give your account access on the{" "}
            <strong>Settings &rarr; Users</strong> screen.
          </p>

          <ul>
            <li>
              <strong>Site:</strong> {user?.info?.whoto ?? "Unavailable"}
            </li>
          </ul>
        </div>
      </DefaultLayout>
    );
  }

  return gated(props.children);
};
